import React, { useState } from "react";
import {
  FileText,
  FlaskConical,
  Pill,
  ClipboardList,
  History,
  Stethoscope,
} from "lucide-react";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { useAuth } from "../../auth/context/AuthContext";
import {
  ehMedico,
  ehMultiprofissional,
  ehEnfermeiro,
} from "../../../shared/utils/permissoesCategoria";
import PedidoExames from "./PedidoExames";
import TextoLivre from "./TextoLivre";
import Receituario from "./Receituario";
import HistoricoPrescricoes, {
  PrescricaoHistorico,
} from "./HistoricoPrescricoes";
import DocumentosEnfermagem from "../../enfermagem/components/DocumentosEnfermagem";

interface AbaPrescricoesProps {
  paciente: Paciente;
}

export default function AbaPrescricoes({ paciente }: AbaPrescricoesProps) {
  const { usuarioLogado } = useAuth();
  const isConvidado = usuarioLogado?.tipo === "convidado";

  // 🔐 B88: sub-abas visíveis por categoria (espelho das firestore.rules).
  // Médico: tudo. Multiprofissional: Texto Livre (relatório multidisciplinar,
  // B92) + Histórico. Administrativo/demais: só Histórico (leitura).
  const medico = ehMedico(usuarioLogado);
  const multiprofissional = ehMultiprofissional(usuarioLogado);
  // B252 (AI_114): área EXCLUSIVA da enfermagem (documentos próprios do serviço).
  // AI_118: visível SÓ p/ enfermeiro de verdade. `ehEnfermeiro` inclui o
  // administrador (ele pode tudo nas rules); como admin/médico têm ehMedico=true,
  // o "&& !medico" exclui ambos — a sub-aba fica restrita a quem é enfermeiro.
  const enfermeiro = ehEnfermeiro(usuarioLogado);
  const enfermeiroExclusivo = enfermeiro && !medico;

  const subAbas: { id: string; label: string; icon: React.ElementType }[] = [
    ...(medico
      ? [
          { id: "exames", label: "Pedido de Exames", icon: FlaskConical },
          { id: "texto", label: "Texto Livre", icon: FileText },
          { id: "receituario", label: "Receituário", icon: Pill },
        ]
      : multiprofissional
      ? [{ id: "texto", label: "Relatório Multidisciplinar", icon: FileText }]
      : []),
    ...(enfermeiroExclusivo
      ? [
          {
            id: "enfermagem",
            label: "Documentos de Enfermagem",
            icon: Stethoscope,
          },
        ]
      : []),
    { id: "historico", label: "Histórico", icon: History },
  ];

  const [subAba, setSubAba] = useState<
    "exames" | "texto" | "receituario" | "enfermagem" | "historico"
  >(subAbas[0].id as any);
  const [dadosIniciais, setDadosIniciais] = useState<any>(null);
  const [historicoKey, setHistoricoKey] = useState(0); // para forçar reload

  // Ao clicar num card do histórico, carrega os dados na sub-aba
  // correspondente — só se a sub-aba existir para esta categoria.
  // Reaproveitar um item do histórico: abre a sub-aba de editor correspondente
  // pré-preenchida. Retorna true se abriu; false se não há editor para o tipo
  // (o card então cai na reimpressão). Controle e antimicrobiano usam o mesmo
  // editor de receituário (que auto-detecta o antimicrobiano pelos medicamentos).
  const handleEditarDoHistorico = (presc: PrescricaoHistorico): boolean => {
    const destino =
      presc.tipo === "receituario-controle" ||
      presc.tipo === "receituario-controle-eletronica" ||
      presc.tipo === "receituario-antimicrobiano"
        ? "receituario"
        : presc.tipo;
    if (!subAbas.some((a) => a.id === destino)) return false;
    setDadosIniciais(presc);
    setSubAba(destino as any);
    return true;
  };

  // Ao gerar PDF com sucesso, incrementa key para forçar reload do histórico
  const handlePrescricaoSalva = () => {
    setHistoricoKey((k) => k + 1);
    setDadosIniciais(null);
  };

  if (isConvidado) {
    return (
      <div
        style={{
          background: "white",
          borderRadius: theme.borderRadius.xl,
          boxShadow: theme.shadows.md,
          padding: "32px",
          textAlign: "center",
        }}
      >
        <ClipboardList
          size={48}
          color={theme.gray[300]}
          style={{ marginBottom: "12px" }}
        />
        <p style={{ color: theme.gray[500], fontFamily: theme.fonts.sans }}>
          Convidados não têm acesso ao módulo de prescrições.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "white",
        borderRadius: theme.borderRadius.xl,
        boxShadow: theme.shadows.md,
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
        }}
      >
        <h2
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: theme.gray[800],
            fontFamily: theme.fonts.sans,
            margin: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <ClipboardList size={18} style={{ marginRight: "8px" }} />
          Prescrições e Documentos
        </h2>
      </div>

      {/* SUB-ABAS */}
      <div
        style={{
          display: "flex",
          gap: "2px",
          marginBottom: "12px",
          borderBottom: `1px solid ${theme.gray[200]}`,
        }}
      >
        {subAbas.map((aba) => {
          const isActive = subAba === aba.id;
          return (
            <button
              key={aba.id}
              onClick={() => {
                setSubAba(aba.id as any);
                if (aba.id !== "historico") setDadosIniciais(null);
              }}
              // 🎨 AI_118 (B258) — sub-abas no estilo "sublinhado" flat (mesmo do
              // prontuário/Pesquisa): ativa = texto azul + linha inferior azul;
              // inativa = cinza (hover escurece). Sem formato-pasta/preenchimento.
              style={{
                padding: "8px 14px",
                flexShrink: 0,
                background: "none",
                color: isActive ? theme.primary[700] : theme.gray[500],
                border: "none",
                borderBottom: isActive
                  ? `2px solid ${theme.primary[600]}`
                  : "2px solid transparent",
                fontSize: "13px",
                fontWeight: 600,
                fontFamily: theme.fonts.sans,
                cursor: "pointer",
                transition: "color 0.2s ease, border-color 0.2s ease",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
              onMouseOver={(e) => {
                if (!isActive) e.currentTarget.style.color = theme.gray[700];
              }}
              onMouseOut={(e) => {
                if (!isActive) e.currentTarget.style.color = theme.gray[500];
              }}
            >
              <aba.icon size={14} />
              {aba.label}
            </button>
          );
        })}
      </div>

      {/* CONTEÚDO */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          maxHeight: "calc(100vh - 340px)",
        }}
      >
        {subAba === "exames" && (
          <PedidoExames
            paciente={paciente}
            dadosIniciais={
              dadosIniciais?.tipo === "exames" ? dadosIniciais : null
            }
            onPrescricaoSalva={handlePrescricaoSalva}
          />
        )}
        {subAba === "texto" && (
          <TextoLivre
            paciente={paciente}
            dadosIniciais={
              dadosIniciais?.tipo === "texto" ? dadosIniciais : null
            }
            onPrescricaoSalva={handlePrescricaoSalva}
          />
        )}
        {subAba === "receituario" && (
          <Receituario
            paciente={paciente}
            dadosIniciais={
              dadosIniciais?.tipo === "receituario" ||
              dadosIniciais?.tipo === "receituario-controle"
                ? dadosIniciais
                : null
            }
            onPrescricaoSalva={handlePrescricaoSalva}
          />
        )}
        {subAba === "enfermagem" && (
          <DocumentosEnfermagem paciente={paciente} />
        )}
        {subAba === "historico" && (
          <HistoricoPrescricoes
            key={historicoKey}
            paciente={paciente}
            onEditar={handleEditarDoHistorico}
          />
        )}
      </div>
    </div>
  );
}
